import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  fetchKategori,
  fetchKelas,
  fetchDokter,
  postCalculate,
  saveCalculation,
  updateCalculation,
  getCalculation,
  AI_DRAFT_KEY,
} from "../lib/api";
import { formatRupiah, ksmForKategori, kategoriShortLabel } from "../lib/format";
import TindakanCard from "../components/TindakanCard";
import ResultTable from "../components/ResultTable";
import SearchableSelect from "../components/SearchableSelect";
import * as XLSX from "xlsx";
import {
  Trash,
  Printer,
  FilePdf,
  FileXls,
  FloppyDisk,
  Calculator as CalcIcon,
  Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const blankTindakan = () => ({
  nama_tindakan: "",
  kategori: "",
  cito: false,
  penyulit: false,
  multiplet: null,
  lokasi: "BEDA",
  operator: "SAMA",
  manajemen_nyeri: 0,
  obat_bhp_ok: 0,
  include_asisten: false,
  include_sewa_alat_khusus: false,
  biaya_tambahan: [],
});

const OPTYPES = [
  { value: "CITO", label: "Cito" },
  { value: "ELEKTIF", label: "Elektif" },
  { value: "PERMINTAAN_WAKTU", label: "Permintaan Waktu" },
];

const PENJAMIN_OPTS = [
  { value: "UMUM", label: "Umum / Asuransi" },
  { value: "BPJSTK", label: "BPJSTK" },
];

// Recompute aggregated_components from tindakan_breakdown (in sync with backend order)
const AGG_MAP = [
  ["Dokter Operator", ["Dokter Operator"]],
  ["Dokter Asisten", ["Dokter Asisten", "Asisten Operator"]],
  ["Dokter Anestesi", ["Dokter Anestesi"]],
  ["Sewa Kamar Operasi", ["Sewa Kamar Operasi"]],
  ["Sewa Kamar RR", ["Sewa Kamar Recovery Room", "Sewa Kamar RR"]],
  ["Sewa Peralatan Operasi", ["Sewa Peralatan Operasi"]],
  ["Sewa Alat Khusus", ["Sewa Alat Khusus"]],
  ["Manajemen Nyeri", ["Manajemen Nyeri"]],
  ["Obat & BHP OK", ["Obat & BHP OK"]],
  ["Jasa Paramedis", ["Jasa Paramedis"]],
  ["Box Bayi Baru Lahir", ["Box Bayi Baru Lahir"]],
];
function recomputeAgg(breakdowns) {
  const base = AGG_MAP.map(([label, includes]) => {
    let total = 0;
    breakdowns.forEach((b) =>
      b.items.forEach((it) => {
        if (!it.is_custom && includes.includes(it.nama_instrumen_grup)) total += it.tarif || 0;
      }),
    );
    return { label, total };
  }).filter((a) => a.total > 0);
  const custom = [];
  breakdowns.forEach((b) =>
    b.items.forEach((it) => {
      if (!it.is_custom) return;
      const found = custom.find((c) => c.label === it.nama_instrumen_grup);
      if (found) found.total += it.tarif || 0;
      else custom.push({ label: it.nama_instrumen_grup, total: it.tarif || 0, is_custom: true });
    }),
  );
  return [...base, ...custom.filter((c) => c.total > 0)];
}

export default function CalculatorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const fromAi = searchParams.get("from") === "ai";
  const [aiMeta, setAiMeta] = useState(null);
  const [namaPasien, setNamaPasien] = useState("");
  const [umur, setUmur] = useState("");
  const [noMr, setNoMr] = useState("");
  const [ruangKelas, setRuangKelas] = useState("");
  const [kelas, setKelas] = useState("KELAS 3");
  const [penjamin, setPenjamin] = useState("UMUM");
  const [dokterOperator, setDokterOperator] = useState("");
  const [dokterAnestesi, setDokterAnestesi] = useState("");
  const [operationType, setOperationType] = useState("ELEKTIF");
  const [jumlahTindakan, setJumlahTindakan] = useState(1);
  const [tindakan, setTindakan] = useState([blankTindakan()]);

  const [kategoriList, setKategoriList] = useState([]);
  const [kelasList, setKelasList] = useState([
    "KELAS 1",
    "KELAS 2",
    "KELAS 3",
    "KELAS VIP",
    "KELAS VVIP",
  ]);
  const [dokterOperatorList, setDokterOperatorList] = useState([]);
  const [dokterAnestesiList, setDokterAnestesiList] = useState([]);
  const [allDokter, setAllDokter] = useState([]);

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchKategori().then(setKategoriList);
    fetchKelas().then(setKelasList).catch(() => {});
    fetchDokter().then(setAllDokter);
  }, []);

  // Load existing calculation when editing from History
  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const doc = await getCalculation(editId);
        const inp = doc.input || {};
        setNamaPasien(doc.nama_pasien || "");
        setUmur(doc.umur || "");
        setNoMr(doc.no_mr || "");
        setRuangKelas(doc.ruang_kelas || "");
        setKelas(doc.kelas || "KELAS 3");
        setPenjamin((doc.penjamin || "UMUM").toUpperCase());
        setDokterOperator(doc.dokter_operator || "");
        setDokterAnestesi(doc.dokter_anestesi || "");
        setOperationType(doc.operation_type || "ELEKTIF");
        const tArr = (inp.tindakan && inp.tindakan.length ? inp.tindakan : [blankTindakan()]).map((t) => ({
          ...blankTindakan(),
          ...t,
        }));
        setJumlahTindakan(tArr.length);
        setTindakan(tArr);
        // Prefill result so edited values from history are visible immediately
        if (doc.result) setResult(doc.result);
        toast.info("Riwayat dimuat. Ubah dan simpan untuk memperbarui.");
      } catch (e) {
        toast.error("Gagal memuat riwayat.");
      }
    })();
    // eslint-disable-next-line
  }, [editId]);

  // Load draft proposal sent from Mode AI
  useEffect(() => {
    if (!fromAi) return;
    try {
      const raw = sessionStorage.getItem(AI_DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      const p = d.proposal || {};
      if (p.kelas) setKelas(p.kelas);
      if (p.penjamin) setPenjamin(String(p.penjamin).toUpperCase());
      if (p.operation_type) setOperationType(p.operation_type);
      if (d.nama_pasien) setNamaPasien(d.nama_pasien);
      if (d.ruang_kelas) setRuangKelas(d.ruang_kelas);
      const tArr = (p.tindakan || []).slice(0, 3).map((t) => ({ ...blankTindakan(), ...t }));
      if (tArr.length) {
        setJumlahTindakan(tArr.length);
        setTindakan(tArr);
      }
      setAiMeta({ model: d.model_label, session_id: d.session_id, ringkasan: p.ringkasan });
      sessionStorage.removeItem(AI_DRAFT_KEY);
      toast.info("Usulan dari Mode AI dimuat. Lengkapi data pasien lalu Hitung.");
    } catch (_) {
      toast.error("Gagal memuat usulan AI.");
    }
    // eslint-disable-next-line
  }, [fromAi]);

  useEffect(() => {
    if (!allDokter.length) return;
    const firstKat = tindakan[0]?.kategori;
    const ksms = ksmForKategori(firstKat);
    const nextOpList = ksms
      ? allDokter.filter((d) => ksms.includes(d.ksm))
      : allDokter;
    setDokterOperatorList(nextOpList);
    // Clear stale operator selection if it's no longer in the filtered list
    if (dokterOperator && !nextOpList.some((d) => d.nama === dokterOperator)) {
      setDokterOperator("");
    }
    setDokterAnestesiList(allDokter.filter((d) => d.ksm === "Spesialis Anesthesi"));
  }, [allDokter, tindakan, dokterOperator]);

  // Auto-generate "Alasan" preview from tindakan list
  const alasanPreview = useMemo(
    () =>
      tindakan
        .filter((t) => t.nama_tindakan || t.kategori)
        .map((t) => {
          const nm = t.nama_tindakan || t.kategori;
          const lbl = kategoriShortLabel(t.kategori);
          return lbl ? `${nm} (${lbl})` : nm;
        })
        .join(", "),
    [tindakan],
  );

  // sync tindakan array length with jumlahTindakan
  useEffect(() => {
    setTindakan((prev) => {
      const next = [...prev];
      while (next.length < jumlahTindakan) next.push(blankTindakan());
      while (next.length > jumlahTindakan) next.pop();
      return next;
    });
  }, [jumlahTindakan]);

  // If operation_type is CITO, set first tindakan cito=true
  useEffect(() => {
    if (operationType === "CITO" && tindakan[0] && !tindakan[0].cito) {
      const arr = [...tindakan];
      arr[0] = { ...arr[0], cito: true };
      setTindakan(arr);
    }
  }, [operationType]); // eslint-disable-line

  const isValid = useMemo(() => {
    if (!namaPasien.trim() || !ruangKelas.trim() || !kelas) return false;
    return tindakan.every((t) => t.kategori);
  }, [namaPasien, ruangKelas, kelas, tindakan]);

  const buildPayload = (overrideResult = null) => ({
    nama_pasien: namaPasien.trim(),
    umur: umur.trim(),
    no_mr: noMr.trim(),
    ruang_kelas: ruangKelas.trim(),
    kelas,
    dpjp: dokterOperator,
    dokter_operator: dokterOperator,
    dokter_anestesi: dokterAnestesi,
    operation_type: operationType,
    penjamin,
    alasan: alasanPreview,
    tindakan: tindakan.map((t) => ({
      ...t,
      biaya_tambahan: (t.biaya_tambahan || []).filter(
        (b) => (b.nama || "").trim() && Number(b.nominal) > 0,
      ),
    })),
    override_result: overrideResult,
    ...(aiMeta
      ? { source: "AI", ai_model: aiMeta.model || "", ai_session_id: aiMeta.session_id || "" }
      : {}),
  });

  const handleCalculate = async () => {
    if (!isValid) {
      toast.error("Lengkapi data pasien dan minimal kategori tiap tindakan.");
      return;
    }
    setLoading(true);
    try {
      const data = await postCalculate(buildPayload());
      setResult(data);
      toast.success("Perhitungan berhasil.");
    } catch (e) {
      toast.error("Gagal menghitung. " + (e?.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!isValid) {
      toast.error("Lengkapi data sebelum menyimpan.");
      return;
    }
    setSaving(true);
    try {
      const doc = editId
        ? await updateCalculation(editId, buildPayload(result))
        : await saveCalculation(buildPayload(result));
      toast.success(editId ? "Riwayat diperbarui." : "Tersimpan ke riwayat.");
      navigate(`/print/${doc.id}`);
    } catch (e) {
      toast.error("Gagal menyimpan. " + (e?.message || ""));
    } finally {
      setSaving(false);
    }
  };

  // ---- Result editing (diskon per instrumen) ----
  const [editMode, setEditMode] = useState(false);

  // Ensure every item has tarif_asli captured (before any discount is applied)
  const ensureTarifAsli = (r) => {
    r.tindakan_breakdown.forEach((b) => {
      b.items.forEach((it) => {
        if (it.tarif_asli == null) it.tarif_asli = it.tarif || 0;
        if (it.diskon_pct == null) it.diskon_pct = 0;
      });
    });
    return r;
  };

  const recomputeTotals = (r) => {
    r.tindakan_breakdown.forEach((b) => {
      b.subtotal = b.items.reduce((s, x) => s + (x.tarif || 0), 0);
    });
    r.aggregated_components = recomputeAgg(r.tindakan_breakdown);
    r.grand_total = r.tindakan_breakdown.reduce(
      (s, b) => s + (b.subtotal || 0),
      0,
    );
    return r;
  };

  const editItemDiskon = (tindakanIdx, itemIdx, pctRaw) => {
    if (!result) return;
    const clone = JSON.parse(JSON.stringify(result));
    ensureTarifAsli(clone);
    let pct = Number(pctRaw);
    if (!Number.isFinite(pct)) pct = 0;
    if (pct < 0) pct = 0;
    if (pct > 100) pct = 100;
    const it = clone.tindakan_breakdown[tindakanIdx].items[itemIdx];
    it.diskon_pct = pct;
    it.tarif = Math.round((it.tarif_asli || 0) * (1 - pct / 100));
    recomputeTotals(clone);
    setResult(clone);
  };

  const handleExportExcel = () => {
    if (!result) {
      toast.error("Hitung dulu sebelum export.");
      return;
    }
    const wb = XLSX.utils.book_new();
    const summary = [
      ["TARIF BIAYA TINDAKAN OPERASI"],
      [],
      ["Nama Pasien", namaPasien],
      ["Umur", umur],
      ["No MR / Registrasi", noMr],
      ["Ruang / Kelas", `${ruangKelas} / ${kelas}`],
      ["Dr DPJP", dokterOperator],
      ["Nama Operasi", operationType],
      ["Alasan", alasanPreview],
      [],
      ["PERKIRAAN BIAYA OPERASI"],
      ...((result.aggregated_components || []).map((a, i) => [`${i + 1}. ${a.label}`, a.total])),
      ["TOTAL", result.grand_total],
    ];
    const ws = XLSX.utils.aoa_to_sheet(summary);
    XLSX.utils.book_append_sheet(wb, ws, "Tarif");

    result.tindakan_breakdown.forEach((b, idx) => {
      const aoa = [
        [`Tindakan ${idx + 1}: ${b.nama_tindakan || b.kategori}`],
        [`Kategori: ${b.kategori} | ${b.kelas}`],
        [],
        ["Nama Instrumen", "Kelas", "Tipe Tindakan Operasi", "Tarif"],
        ...b.items.map((it) => [it.nama_instrumen, it.kelas, it.kondisi, it.tarif]),
        ["", "", "Subtotal", b.subtotal],
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(aoa);
      XLSX.utils.book_append_sheet(wb, ws2, `Tindakan ${idx + 1}`);
    });

    const filename = `Tarif_Operasi_${(namaPasien || "Pasien").replace(/\s+/g, "_")}_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success("File Excel diunduh.");
  };

  const grandTotal = result?.grand_total ?? 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* LEFT: Form + Results */}
      <div className="col-span-1 lg:col-span-8 xl:col-span-9 flex flex-col gap-6">
        {aiMeta && (
          <div
            className="bg-violet-50 border border-violet-200 text-violet-900 rounded px-4 py-3 text-sm flex items-start gap-3 no-print"
            data-testid="ai-draft-banner"
          >
            <Sparkle size={18} weight="duotone" className="mt-0.5 shrink-0" />
            <div>
              <div className="font-bold">Usulan dari Mode AI dimuat</div>
              <div className="text-xs text-violet-800 mt-0.5">
                {aiMeta.ringkasan || "Periksa kategori, kondisi & biaya tambahan, lalu klik Hitung."}
                {aiMeta.model ? ` · Model: ${aiMeta.model}` : ""}
              </div>
            </div>
          </div>
        )}
        <div
          className="bg-white border border-slate-200 rounded shadow-sm p-5 md:p-6 no-print"
          data-testid="patient-card"
        >
          <div className="pb-4 mb-4 border-b border-slate-100">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">
              Data Pasien
            </div>
            <div className="text-base md:text-lg font-semibold text-slate-900">
              Informasi Pasien & Operator
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Nama Pasien <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={namaPasien}
                onChange={(e) => setNamaPasien(e.target.value)}
                placeholder="Mis. MUHAMAD SANWANI"
                className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0A3F9A]"
                data-testid="input-nama-pasien"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Umur
              </label>
              <input
                type="text"
                value={umur}
                onChange={(e) => setUmur(e.target.value)}
                placeholder="53 Tahun"
                className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0A3F9A]"
                data-testid="input-umur"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                No MR / Registrasi
              </label>
              <input
                type="text"
                value={noMr}
                onChange={(e) => setNoMr(e.target.value)}
                placeholder="30-06-65"
                className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0A3F9A]"
                data-testid="input-no-mr"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Ruang <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={ruangKelas}
                onChange={(e) => setRuangKelas(e.target.value)}
                placeholder="Mawar 302"
                className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0A3F9A]"
                data-testid="input-ruang"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Kelas Perawatan <span className="text-red-600">*</span>
              </label>
              <select
                value={kelas}
                onChange={(e) => setKelas(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0A3F9A]"
                data-testid="select-kelas"
              >
                {kelasList.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Penjamin <span className="text-red-600">*</span>
              </label>
              <select
                value={penjamin}
                onChange={(e) => {
                  setPenjamin(e.target.value);
                  // Reset tindakan kategori as list differs
                  setTindakan((prev) =>
                    prev.map((t) => ({ ...t, kategori: "", multiplet: null })),
                  );
                  setResult(null);
                }}
                className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0A3F9A]"
                data-testid="select-penjamin"
              >
                {PENJAMIN_OPTS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Dokter Operator
              </label>
              <SearchableSelect
                value={dokterOperator}
                onChange={setDokterOperator}
                options={dokterOperatorList.map((d) => ({
                  value: d.nama,
                  label: `${d.nama} - ${d.ksm}`,
                }))}
                placeholder="Cari atau pilih dokter operator..."
                testid="select-dokter-operator"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Dokter Anestesi
              </label>
              <SearchableSelect
                value={dokterAnestesi}
                onChange={setDokterAnestesi}
                options={dokterAnestesiList.map((d) => d.nama)}
                placeholder="Cari atau pilih dokter anestesi..."
                testid="select-dokter-anestesi"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Tipe Operasi
              </label>
              <div className="flex gap-2 flex-wrap" data-testid="optype-group">
                {OPTYPES.map((o) => (
                  <button
                    type="button"
                    key={o.value}
                    onClick={() => setOperationType(o.value)}
                    className={`px-3 py-2 text-xs font-bold rounded border transition-colors ${
                      operationType === o.value
                        ? "bg-[#0A3F9A] text-white border-[#0A3F9A]"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                    }`}
                    data-testid={`btn-optype-${o.value.toLowerCase()}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Jumlah Tindakan
              </label>
              <select
                value={jumlahTindakan}
                onChange={(e) => setJumlahTindakan(Number(e.target.value))}
                className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0A3F9A]"
                data-testid="select-jumlah-tindakan"
              >
                <option value={1}>1 Tindakan</option>
                <option value={2}>2 Tindakan</option>
                <option value={3}>3 Tindakan</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6 no-print">
          {tindakan.map((t, i) => (
            <TindakanCard
              key={i}
              index={i}
              data={t}
              kategoriList={kategoriList}
              onChange={(next) => {
                const arr = [...tindakan];
                arr[i] = next;
                setTindakan(arr);
              }}
            />
          ))}
        </div>

        <div className="flex flex-wrap gap-3 no-print">
          <button
            onClick={handleCalculate}
            disabled={loading}
            className="bg-[#0A3F9A] text-white px-6 py-3 rounded hover:bg-[#082F73] transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="btn-hitung"
          >
            <CalcIcon size={18} weight="duotone" />
            {loading ? "Menghitung..." : "Hitung Estimasi Biaya"}
          </button>
          <button
            onClick={() => {
              setTindakan(
                Array(jumlahTindakan).fill(0).map(() => blankTindakan()),
              );
              setResult(null);
            }}
            className="bg-white border border-slate-300 text-slate-700 px-4 py-3 rounded hover:bg-slate-50 text-sm font-medium flex items-center justify-center gap-2"
            data-testid="btn-reset"
          >
            <Trash size={16} /> Reset Tindakan
          </button>
        </div>

        {result && (
          <div className="flex flex-col gap-6" data-testid="result-section">
            <div className="bg-white border border-slate-200 rounded shadow-sm p-5 flex items-center justify-between flex-wrap gap-3 no-print">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editMode}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setEditMode(on);
                    if (on && result) {
                      const clone = JSON.parse(JSON.stringify(result));
                      ensureTarifAsli(clone);
                      setResult(clone);
                    }
                  }}
                  className="accent-amber-600"
                  data-testid="toggle-edit-mode"
                />
                Mode Edit Harga (diskon per instrumen)
              </label>
              {editMode && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded">
                  Ubah tarif per baris untuk memberi diskon. Kop surat akan
                  menggunakan nilai yang sudah diedit.
                </div>
              )}
            </div>
            <div className="bg-white border border-slate-200 rounded shadow-sm p-5">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">
                Estimasi untuk
              </div>
              <div className="text-xl font-bold text-slate-900">{namaPasien}</div>
              <div className="text-sm text-slate-600 mt-1">
                {ruangKelas} · {kelas} · Penjamin:{" "}
                {penjamin === "BPJSTK" ? "BPJSTK" : "Umum / Asuransi"}
                {dokterOperator ? ` · DPJP: ${dokterOperator}` : ""}
              </div>
            </div>
            {result.tindakan_breakdown.map((b, i) => (
              <ResultTable
                key={i}
                breakdown={b}
                index={i}
                editable={editMode}
                onEditDiskon={(itemIdx, pct) =>
                  editItemDiskon(i, itemIdx, pct)
                }
              />
            ))}
            <div className="bg-blue-900 text-white rounded shadow-sm p-5 md:p-6 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-blue-200">
                  Grand Total Estimasi
                </div>
                <div className="text-xs text-blue-200 mt-1">
                  {result.tindakan_breakdown.length} tindakan
                </div>
              </div>
              <div
                className="text-3xl md:text-4xl font-black tabular-nums tracking-tighter"
                data-testid="text-grand-total"
              >
                {formatRupiah(result.grand_total)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT: Sticky summary */}
      <aside className="col-span-1 lg:col-span-4 xl:col-span-3 no-print">
        <div className="sticky top-6 h-fit flex flex-col gap-4">
          <div className="bg-white border border-slate-200 rounded shadow-sm p-5">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">
              Ringkasan
            </div>
            <div className="text-sm text-slate-700 mt-2">
              {result
                ? `${result.tindakan_breakdown.length} tindakan dihitung`
                : "Belum ada perhitungan"}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">
                Grand Total
              </div>
              <div
                className="text-3xl md:text-4xl font-black tabular-nums tracking-tighter text-blue-900 mt-1"
                data-testid="text-grand-total-sidebar"
              >
                {formatRupiah(grandTotal)}
              </div>
            </div>
            {result?.aggregated_components && (
              <div className="mt-4 space-y-1.5">
                {result.aggregated_components.map((a, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between text-xs border-b border-slate-100 pb-1.5"
                  >
                    <span className="text-slate-700">{a.label}</span>
                    <span className="tabular-nums font-medium">
                      {formatRupiah(a.total)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded shadow-sm p-5">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">
              Aksi
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleSave}
                disabled={saving || !result}
                className="bg-[#057A55] text-white px-4 py-2 rounded hover:bg-[#046148] transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="btn-simpan-riwayat"
              >
                <FloppyDisk size={16} weight="duotone" />
                {saving ? "Menyimpan..." : "Simpan & Buka Kop Surat"}
              </button>
              <button
                onClick={() => window.print()}
                disabled={!result}
                className="bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded hover:bg-slate-50 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="btn-print"
              >
                <Printer size={16} weight="duotone" /> Print Tabel
              </button>
              <button
                onClick={() => window.print()}
                disabled={!result}
                className="bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded hover:bg-slate-50 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="btn-export-pdf"
              >
                <FilePdf size={16} weight="duotone" /> Export PDF
              </button>
              <button
                onClick={handleExportExcel}
                disabled={!result}
                className="bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded hover:bg-slate-50 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="btn-export-excel"
              >
                <FileXls size={16} weight="duotone" /> Export Excel
              </button>
            </div>
            <div className="mt-3 text-[11px] text-slate-500">
              Simpan estimasi untuk membuka tampilan kop surat resmi RS.
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
